import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CheckCircle2, XCircle, Clock, Shield } from "lucide-react";
import { INR } from "@/lib/constants";
import { toast } from "sonner";

const ACTION_LABEL = {
  MEMBERSHIP_CREATE: "Create Membership",
  MEMBERSHIP_CANCEL: "Cancel Membership",
  REFUND: "Issue Refund",
  LEAD_DELETE: "Delete Lead",
  MEMBER_EDIT: "Edit Member",
  MEMBER_DEACTIVATE: "Deactivate Member",
};

export default function ApprovalsPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = React.useState("PENDING");

  const { data: items = [] } = useQuery({
    queryKey: ["approvals", tab],
    queryFn: async () => (await apiClient.get("/approvals", { params: tab === "ALL" ? {} : { status_filter: tab } })).data,
  });

  const decide = useMutation({
    mutationFn: async ({ id, decision }) => (await apiClient.patch(`/approvals/${id}`, { decision })).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["approvals"] });
      toast.success(`${vars.decision === "APPROVED" ? "Approved" : "Rejected"}`);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="approvals-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold flex items-center gap-2">
            <Shield size={12} /> Marker-Checker
          </div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Approvals</h1>
          <div className="text-zinc-500 text-sm mt-1">
            Critical actions (membership · refund · lead deletion) require Super Admin sign-off.
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="PENDING" data-testid="tab-pending" className="data-[state=active]:bg-volt data-[state=active]:text-black">Pending</TabsTrigger>
          <TabsTrigger value="APPROVED" data-testid="tab-approved" className="data-[state=active]:bg-volt data-[state=active]:text-black">Approved</TabsTrigger>
          <TabsTrigger value="REJECTED" data-testid="tab-rejected" className="data-[state=active]:bg-volt data-[state=active]:text-black">Rejected</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Panel>
            {items.length === 0 ? (
              <div className="text-zinc-600 text-sm py-12 text-center">No items.</div>
            ) : (
              <div className="space-y-2">
                {items.map((a) => (
                  <ApprovalRow key={a.id} item={a} canDecide={user?.role === "SUPER_ADMIN" && a.status === "PENDING"}
                    onApprove={() => decide.mutate({ id: a.id, decision: "APPROVED" })}
                    onReject={() => decide.mutate({ id: a.id, decision: "REJECTED" })} />
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ApprovalRow({ item, canDecide, onApprove, onReject }) {
  const color = item.status === "APPROVED" ? "#00E676" : item.status === "REJECTED" ? "#FF3B30" : "#FFEA00";
  return (
    <div data-testid={`approval-${item.id}`} className="bg-zinc-950 border border-zinc-900 rounded-sm p-3">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 grid place-items-center rounded-sm" style={{ background: `${color}22`, color }}>
          {item.status === "PENDING" ? <Clock size={16} /> : item.status === "APPROVED" ? <CheckCircle2 size={16} /> : <XCircle size={16} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{ACTION_LABEL[item.action_type] || item.action_type}</div>
          <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
            By {item.requested_by_name || "—"} · {new Date(item.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </div>
          {item.payload && Object.keys(item.payload).length > 0 && (
            <div className="text-xs text-zinc-400 mt-1 font-mono-cmd">
              {Object.entries(item.payload).slice(0, 4).map(([k, v]) => (
                <span key={k} className="mr-3">{k}: <span className="text-white">{typeof v === "number" && k.toLowerCase().includes("amount") ? INR(v) : String(v).slice(0, 40)}</span></span>
              ))}
            </div>
          )}
          {item.review_note && <div className="text-xs text-zinc-500 mt-1">Note: {item.review_note}</div>}
        </div>
        <span className="text-[10px] uppercase tracking-[0.16em] font-bold px-2 py-1 rounded-sm" style={{ background: `${color}22`, color }}>
          {item.status}
        </span>
        {canDecide && (
          <>
            <Button size="sm" onClick={onApprove} data-testid={`approve-${item.id}`}
              className="bg-volt hover:bg-volt-hover text-black font-bold uppercase">
              <CheckCircle2 size={12} className="mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" onClick={onReject} data-testid={`reject-${item.id}`}
              className="bg-zinc-900 border-zinc-800 text-red-400 hover:border-red-500">
              <XCircle size={12} className="mr-1" /> Reject
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
